// App.jsx
import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Toaster } from 'react-hot-toast';

import Sidebar from './components/layout/Sidebar';
import Header from './components/layout/Header';
import Dashboard from './components/dashboard/Dashboard';
import IncidentList from './components/incidents/IncidentList';
import IncidentDetail from './components/incidents/IncidentDetail';
import CreateIncident from './components/incidents/CreateIncident';
import AIAssistant from './components/ai/AIAssistant';
import Profile from './components/profile/Profile';
import Settings from './components/settings/Settings';

import Login from './components/auth/Login';
import SignUp from './components/auth/SignUp';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import VerifyEmail from './components/auth/VerifyEmail';
import TwoFactorAuth from './components/auth/TwoFactorAuth';
import AcceptInvite from './components/auth/AcceptInvite';

import { ThemeProvider } from './context/ThemeContext';
import { IncidentProvider } from './context/IncidentContext';
import { AuthProvider, useAuth } from './context/AuthContext';

// Layout with Sidebar, Header, AI Assistant, and AnimatePresence
const MainLayout = ({ sidebarCollapsed, onToggleSidebar }) => (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
        <Sidebar collapsed={sidebarCollapsed} />
        <div className="flex-1 flex flex-col overflow-hidden">
            <Header
                onToggleSidebar={onToggleSidebar}
                sidebarCollapsed={sidebarCollapsed}
            />
            <main className="flex-1 overflow-hidden relative">
                <AnimatePresence mode="wait">
                    <Outlet />
                </AnimatePresence>
            </main>
        </div>
        <AIAssistant />
    </div>
);

// Animated wrapper for route transitions
const AnimatedPage = ({ children }) => {
    const location = useLocation();
    return (
        <motion.div
            key={location.pathname}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            className="h-full"
        >
            {children}
        </motion.div>
    );
};

// ProtectedRoute wrapper

const ProtectedRoute = () => {
    const { user, loading } = useAuth();

    if (loading) return null;

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    return <Outlet />;
};

function App() {
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    return (
        <Router>
            <ThemeProvider>
                <AuthProvider>
                    <IncidentProvider>
                        <Toaster position="top-right" />

                        <Routes>
                            {/* ---------- PUBLIC ---------- */}
                            <Route path="/login" element={<Login />} />
                            <Route path="/signup" element={<SignUp />} />
                            <Route path="/forgot-password" element={<ForgotPassword />} />
                            <Route path="/reset-password" element={<ResetPassword />} />
                            <Route path="/verify-email" element={<VerifyEmail />} />
                            <Route path="/2fa" element={<TwoFactorAuth />} />
                            <Route path="/accept-invite" element={<AcceptInvite />} />

                            {/* ---------- PROTECTED ---------- */}
                            <Route element={<ProtectedRoute />}>
                                <Route
                                    element={
                                        <MainLayout
                                            sidebarCollapsed={sidebarCollapsed}
                                            onToggleSidebar={() =>
                                                setSidebarCollapsed(!sidebarCollapsed)
                                            }
                                        />
                                    }
                                >
                                    <Route path="/" element={<Dashboard />} />
                                    <Route path="/incidents" element={<IncidentList />} />
                                    <Route path="/incidents/new" element={<CreateIncident />} />
                                    <Route path="/incidents/:id" element={<IncidentDetail />} />
                                    <Route path="/profile" element={<Profile />} />
                                    <Route path="/settings" element={<Settings />} />
                                </Route>
                            </Route>

                            {/* ---------- FALLBACK ---------- */}
                            <Route path="*" element={<Navigate to="/login" replace />} />
                        </Routes>

                    </IncidentProvider>
                </AuthProvider>
            </ThemeProvider>
        </Router>
    );
}

export default App;